// @ts-nocheck
import React, { useEffect, useState, useCallback, useRef } from 'react';

import Search from './Search';
import Overlay from './Overlay';
import Markers from './Markers';
import OverlayContext from './context/OverlayContext';
import {
  getLaundry,
  getNearbyBuses,
  getPcs,
  getOutlets,
} from './data/apiClient';

import Mazemap from '@itpi/mazemap';
import '@itpi/mazemap/mazemap.min.css';

interface ILocation {
  lng: number;
  lat: number;
  accuracy?: number;
}

interface IConfig {
  search?: boolean;
  buses?: boolean;
  laundry?: boolean;
  outlets?: boolean;
  pcs?: boolean;
  zlevel?: boolean;
  pitch?: number;
}

const defaultConfig = {
  search: false,
  buses: false,
  laundry: false,
  pcs: false,
  outlets: false,
  zlevel: false,
  pitch: 0,
};

export const MazeMap: React.FC<{
  id?: number;
  location?: ILocation;
  config?: IConfig;
  poi?: (ILocation | number)[];
}> = ({ id = 0, location, config, poi }) => {
  const [dot, setDot] = useState(null);
  const mapRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  const [buses, setBuses] = useState(null);
  const [pcs, setPcs] = useState(null);
  const [laundry, setLaundry] = useState(null);
  const [outlets, setOutlets] = useState(null);
  const [markers, setMarkers] = useState<any[]>([]);
  const [mapCenter, setMapCenter] = useState<ILocation>(location);
  const [mapCenterTimer, setMapCenterTimer] = useState<number>(0);
  const [loading, setLoading] = useState<any>({ buses: false });

  const currentConfig = { ...defaultConfig, ...config };

  const makeMazeMapInstance = (options: Object): Mazemap.Map => {
    const mapOptions = {
      ...options,
      container: `mazemap-container-${id}`,
      campuses: 341,
      autoSetRTLTextPlugin: false,
      center: location ? location : { lng: -2.7842062, lat: 54.0064025 },
      zoom: 17,
      zLevel: 0,
      pitch: currentConfig.pitch,
      doubleClickZoom: false,
      scrollZoom: true,
      touchZoomRotate: true,
      zLevelControl: false,
    };

    let mazeMapObject = new Mazemap.Map(mapOptions);

    mazeMapObject.on('load', (e: any) => {
      mazeMapObject.ready = true;

      const newHighlighter = new Mazemap.Highlighter(e.target, {
        showOutline: true,
        showFill: true,
        outlineColor: Mazemap.Util.Colors.MazeColors.MazeBlue,
        fillColor: Mazemap.Util.Colors.MazeColors.MazeBlue,
      });
      newHighlighter.showFill();
      newHighlighter.showOutline();

      var floorBar = new Mazemap.ZLevelBarControl({
        autoUpdate: true,
        maxHeight: 200,
      });
      e.target.addControl(floorBar, 'middle-left');

      mapRef.current = e.target;
      setMapReady(true);

      mapRef.current.on('move', (e) => {
        setMapCenter(e.target.transform._center);
        setMapCenterTimer(1);
        setLoading(prev => {return {...prev, buses: true}});
      });
    });

    return mazeMapObject;
  };

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    if (currentConfig.laundry)
      getLaundry(signal).then(response => setLaundry(response));
    if (currentConfig.pcs) getPcs(signal).then(response => setPcs(response));
    if (currentConfig.outlets)
      getOutlets(signal).then(response => setOutlets(response));

    return () => controller.abort();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    if (currentConfig.buses && mapCenterTimer === 0) {
      setLoading(prev => {return {...prev, buses: true}});
      getNearbyBuses(signal, mapCenter?.lat, mapCenter?.lng).then(response => {
        setLoading(prev => {return {...prev, buses: false}});
        setBuses(prev => {
          if (Array.isArray(response) && response.length > 0) {
            return response;
          } else {
            return prev;
          }
        });
      }
      );
    }

    return () => controller.abort();
  }, [mapCenter, mapCenterTimer]);

  useEffect(() => {
    const interval: any = setInterval(() => {
      setMapCenterTimer(prev => {
        if (prev > 0) {
          return prev - 0.5;
        } else {
          return 0;
        }
      });
    }, 500);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    mapRef.current = makeMazeMapInstance({});
  }, []);

  useEffect(() => {
    if (mapReady && location) {
      if (!dot) {
        //@ts-ignore
        var blueDot = new Mazemap.BlueDot({
          zLevel: 1,
          accuracyCircle: location.accuracy, //Check if accuracy exists
        })
          .setLngLat(location)
          .setAccuracy(location.accuracy)
          .addTo(mapRef.current);
        setDot(blueDot);
      } else {
        dot.setLngLat(location, { animate: true, duration: 400 });
      }
    }
  }, [location, mapReady]);

  useEffect(() => {
    if (mapReady && poi && poi.length > 0) {
      const resolvePoi = async plannonId => {
        const response = await fetch(
          `https://api.mazemap.com/api/pois/?identifier=${plannonId}&srid=4326&lang=&campuscollectiontag=lancaster`
        );
        const responseJSON = await response.json();

        if (response.ok) {
          var firstPoi = responseJSON.pois[0];
          var geoJSON = {
            geometry: firstPoi.geometry,
            properties: {
              ...firstPoi,
            },
            type: 'Feature',
          };

          var lngLat = Mazemap.Util.getPoiLngLat(geoJSON);
          return { lng: lngLat.lng, lat: lngLat.lat };
        } else {
          return null;
        }
      };

      const populateMarkers = async () => {
        const promises = poi.map(item => {
          if (typeof item === 'number') return resolvePoi(item);

          return new Promise(resolve => resolve(item));
        });

        const newMarkers = await Promise.all(promises);
        setMarkers(
          newMarkers.map(newMarker => {
            return { location: newMarker };
          })
        );
      };

      populateMarkers();
    }
  }, [poi, mapReady]);

  const placePoiMarker = poi => {
    // Get a center point for the POI, because the data can return a polygon instead of just a point sometimes
    var lngLat = Mazemap.Util.getPoiLngLat(poi);
    var zLevel = poi.properties.zValue;

    const resultMarker = new Mazemap.MazeMarker({
      color: 'rgb(253, 117, 38)',
      innerCircle: true,
      innerCircleColor: '#FFF',
      size: 34,
      innerCircleScale: 0.5,
      zLevel: 1,
    });

    resultMarker
      .setLngLat(lngLat)
      .setZLevel(poi.properties.zValue)
      .addTo(mapRef.current);

    mapRef.current.zLevel = zLevel;

    mapRef.current.flyTo({ center: lngLat, zoom: 19, duration: 2000 });
  };

  return (
    <OverlayContext.Provider
      value={{
        id,
        mapRef,
        mapReady,
        location,
        config,
        buses,
        pcs,
        laundry,
        outlets,
        markers,
        setMarkers,
        search: currentConfig.search,
      }}
    >
      <div
        style={{
          height: '100%',
          width: '100%',
          position: 'absolute',
          top: 0,
          left: 0,
        }}
      >
        <div
          id={`mazemap-container-${id}`}
          style={{
            height: '100%',
            width: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
          }}
        ></div>

        {currentConfig.search && <Search onFind={poi => placePoiMarker(poi)} />}
        <Overlay loading={loading}/>
        <Markers />
      </div>
    </OverlayContext.Provider>
  );
};
